import React, { useState, useMemo, useEffect } from 'react'
import {
  Input,
  Button,
  Select,
  Collapse,
  Checkbox,
  Radio,
  Space,
  Tag,
  Divider,
  Typography,
  theme,
  Badge,
  Slider,
  Card,
  Tooltip,
  Row,
  Col
} from 'antd'
import {
  SearchOutlined,
  CaretRightOutlined,
  CloseOutlined,
  ClearOutlined,
  FilterOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  GlobalOutlined,
  UserOutlined,
  VideoCameraOutlined,
  HomeOutlined,
  CalendarOutlined,
  EnvironmentOutlined
} from '@ant-design/icons'
import { readProfessionsSpecialties, readSearchTypes } from '../api/searchApi'
import GoogleAddress from '../../reusable-components/GoogleAddress'
import { useSelector, useDispatch } from 'react-redux'
import { geocodeByAddress } from 'react-google-places-autocomplete'
import { setSearchResults } from '../utils/searchReducer'

const { Option } = Select
const { Panel } = Collapse
const { Title, Text } = Typography

const SearchRefinement = ({
  onSearch,
  onFilterChange,
  searchFilters = {},
  loading = false,
  searchType,
  setSearchType
}) => {
  const [searchTypes, setSearchTypes] = useState([])
  const [address, setAddress] = useState(searchFilters.address || '')
  const [placeId, setPlaceId] = useState(searchFilters.placeId || '')
  const [location, setLocation] = useState(searchFilters.location || null)
  const [maxDistance, setMaxDistance] = useState(searchFilters.maxDistance || 25)
  const [isLoadingLocation, setIsLoadingLocation] = useState(false)
  const [hasManuallySetLocation, setHasManuallySetLocation] = useState(false)
  const [loadingStartTime, setLoadingStartTime] = useState(null)
  const [isUILoading, setIsUILoading] = useState(false)
  const { token } = theme.useToken()
  const dispatch = useDispatch()
  const patientAddress = useSelector((state) => state.auth.agiliteUser.residentialAddress)

  useEffect(() => {
    fetchProfessionsSpecialties()
  }, [])

  // Track loading state with minimum animation time
  useEffect(() => {
    if (loading) {
      setLoadingStartTime(Date.now())
      setIsUILoading(true)
    } else if (loadingStartTime) {
      // Ensure minimum loading time of 2 seconds for full animation
      const elapsed = Date.now() - loadingStartTime
      const minimumLoadingTime = 2000 // 2 seconds

      if (elapsed < minimumLoadingTime) {
        const remainingTime = minimumLoadingTime - elapsed
        setTimeout(() => {
          setIsUILoading(false)
        }, remainingTime)
      } else {
        setIsUILoading(false)
      }
    }
  }, [loading, loadingStartTime])

  // Auto-set patient address as default location
  useEffect(() => {
    const setPatientLocation = async () => {
      // Only set if patient has an address, no location is currently set, user hasn't manually set location, and we're not already loading
      if (patientAddress && !location && !hasManuallySetLocation && !isLoadingLocation) {
        setIsLoadingLocation(true)
        try {
          const results = await geocodeByAddress(patientAddress)

          if (results && results.length > 0) {
            const result = results[0]
            const newLocation = {
              address: patientAddress,
              place_id: result.place_id
            }

            setAddress(patientAddress)
            setPlaceId(result.place_id)
            setLocation(newLocation)

            // Save auto-populated location to Redux state
            updateLocationInRedux({
              address: patientAddress,
              placeId: result.place_id,
              location: newLocation
            })
          }
        } catch (error) {
          console.error('Error geocoding patient address:', error)
          // Silently fail - don't show error to user for auto-population
        } finally {
          setIsLoadingLocation(false)
        }
      }
    }

    setPatientLocation()
  }, [patientAddress, location, hasManuallySetLocation, isLoadingLocation])

  const fetchProfessionsSpecialties = async () => {
    try {
      const tmpSearchTypes = await readSearchTypes()
      setSearchTypes(tmpSearchTypes)
    } catch (error) {}
  }

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0
    if (searchFilters.consultType?.length > 0) count += searchFilters.consultType.length
    if (searchFilters.timeSlot) count += 1
    if (searchFilters.languages?.length > 0) count += searchFilters.languages.length
    if (searchFilters.gender) count += 1
    // Note: location and distance are not counted as they're always visible when location is selected
    return count
  }, [searchFilters])

  const handleNewSearch = () => {
    const searchParams = {
      ...searchFilters,
      searchType,
      address,
      placeId,
      location,
      maxDistance
    }

    console.log('Performing new search with filters:', searchParams)
    onSearch?.(searchParams)
  }

  const handleFilterChange = (filterType, value) => {
    onFilterChange?.(filterType, value)
  }

  const handleSearchTypeChange = (value) => {
    console.log('SearchType changed to:', value)
    console.log('Current searchType prop:', searchType)

    // Update Redux state first
    onFilterChange?.('searchType', value)

    // Automatically search again with the new search type
    // Make sure to use the new value and override any old searchType in searchFilters
    const searchParams = {
      ...searchFilters,
      searchType: value,
      address,
      placeId,
      location,
      maxDistance
    }

    console.log('Searching with params:', searchParams)
    onSearch?.(searchParams)
  }

  const handleLocationChange = (value) => {
    setHasManuallySetLocation(true) // Mark as manually set by user
    if (value && value.value) {
      const newPlaceId = value.value.place_id
      const newAddress = value.label
      const newLocation = {
        address: newAddress,
        place_id: newPlaceId
      }

      setPlaceId(newPlaceId)
      setAddress(newAddress)
      setLocation(newLocation)

      // Save to Redux state
      updateLocationInRedux({
        placeId: newPlaceId,
        address: newAddress,
        location: newLocation
      })
    } else {
      setPlaceId('')
      setAddress('')
      setLocation(null)

      // Save to Redux state
      updateLocationInRedux({
        placeId: '',
        address: '',
        location: null
      })
    }
  }

  const clearAllFilters = () => {
    onFilterChange?.('consultType', [])
    onFilterChange?.('timeSlot', '')
    onFilterChange?.('languages', [])
    onFilterChange?.('gender', '')
    onFilterChange?.('searchType', 'general_practitioner') // Reset searchType too

    // Reset location and distance
    setAddress('')
    setPlaceId('')
    setLocation(null)
    setMaxDistance(25)
    setIsLoadingLocation(false)
    setHasManuallySetLocation(false) // Allow auto-population again

    // Save location reset to Redux state
    updateLocationInRedux({
      address: '',
      placeId: '',
      location: null,
      maxDistance: 25
    })
  }

  // Helper function to format filter values with proper case
  const formatFilterValue = (filterType, value) => {
    const formatters = {
      consultType: {
        inPerson: 'In-Person',
        virtual: 'Virtual',
        homeVisit: 'Home Visit'
      },
      timeSlot: {
        morning: 'Morning',
        afternoon: 'Afternoon',
        evening: 'Evening'
      },
      languages: {
        english: 'English',
        afrikaans: 'Afrikaans',
        zulu: 'Zulu',
        xhosa: 'Xhosa',
        sotho: 'Sotho'
      },
      gender: {
        male: 'Male Doctor',
        female: 'Female Doctor',
        any: 'No Preference'
      }
    }

    return formatters[filterType]?.[value] || value
  }

  // Helper function to get filter category colors
  const getFilterColor = (filterType, value) => {
    const colors = {
      consultType: {
        inPerson: '#52c41a',
        virtual: '#1890ff',
        homeVisit: '#fa8c16'
      },
      timeSlot: token.colorWarning,
      languages: token.colorSuccess,
      gender: token.colorInfo
    }

    if (filterType === 'consultType') {
      return colors.consultType[value] || '#1890ff'
    }

    return colors[filterType] || '#1890ff'
  }

  // Helper function to update Redux state for location-related parameters
  const updateLocationInRedux = (updates) => {
    Object.entries(updates).forEach(([key, value]) => {
      onFilterChange?.(key, value)
    })
  }

  // Synchronize local state with Redux state when it changes
  useEffect(() => {
    if (searchFilters.address !== address) setAddress(searchFilters.address || '')
    if (searchFilters.placeId !== placeId) setPlaceId(searchFilters.placeId || '')
    if (JSON.stringify(searchFilters.location) !== JSON.stringify(location)) {
      setLocation(searchFilters.location || null)
    }
    if (searchFilters.maxDistance !== maxDistance) setMaxDistance(searchFilters.maxDistance || 25)
  }, [
    searchFilters.address,
    searchFilters.placeId,
    searchFilters.location,
    searchFilters.maxDistance,
    address,
    placeId,
    location,
    maxDistance
  ])

  const filterSections = [
    {
      key: 'consultType',
      label: 'Type of Consultation',
      icon: <VideoCameraOutlined style={{ color: token.colorPrimary }} />,
      count: searchFilters.consultType?.length || 0,
      children: (
        <div style={{ padding: '8px 0' }}>
          <Checkbox.Group
            style={{ width: '100%' }}
            value={searchFilters.consultType || []}
            onChange={(values) => handleFilterChange('consultType', values)}
            disabled={isUILoading}
          >
            <Space direction='vertical' size={12} style={{ width: '100%' }}>
              <Checkbox value='inPerson' style={{ fontSize: '14px' }}>
                <Space>
                  <UserOutlined style={{ color: '#52c41a' }} />
                  <span>In-Person Consultation</span>
                </Space>
              </Checkbox>
              <Checkbox value='virtual' style={{ fontSize: '14px' }}>
                <Space>
                  <VideoCameraOutlined style={{ color: '#1890ff' }} />
                  <span>Virtual Consultation</span>
                </Space>
              </Checkbox>
              <Checkbox value='homeVisit' style={{ fontSize: '14px' }}>
                <Space>
                  <HomeOutlined style={{ color: '#fa8c16' }} />
                  <span>Home Visit</span>
                </Space>
              </Checkbox>
            </Space>
          </Checkbox.Group>
        </div>
      )
    },
    {
      key: 'dateTime',
      label: 'Time Availability',
      icon: <ClockCircleOutlined style={{ color: token.colorWarning }} />,
      count: searchFilters.timeSlot ? 1 : 0,
      children: (
        <div style={{ padding: '8px 0' }}>
          <Radio.Group
            style={{ width: '100%' }}
            value={searchFilters.timeSlot}
            onChange={(e) => handleFilterChange('timeSlot', e.target.value)}
          >
            <Space direction='vertical' size={12} style={{ width: '100%' }}>
              <Radio value='morning' style={{ fontSize: '14px' }}>
                <Space>
                  <span style={{ color: '#fa8c16' }}>🌅</span>
                  <span>Morning (6AM - 12PM)</span>
                </Space>
              </Radio>
              <Radio value='afternoon' style={{ fontSize: '14px' }}>
                <Space>
                  <span style={{ color: '#fadb14' }}>☀️</span>
                  <span>Afternoon (12PM - 6PM)</span>
                </Space>
              </Radio>
              <Radio value='evening' style={{ fontSize: '14px' }}>
                <Space>
                  <span style={{ color: '#1e40af' }}>🌙</span>
                  <span>Evening (6PM - 12AM)</span>
                </Space>
              </Radio>
            </Space>
          </Radio.Group>
        </div>
      )
    },
    {
      key: 'languages',
      label: 'Languages',
      icon: <GlobalOutlined style={{ color: token.colorSuccess }} />,
      count: searchFilters.languages?.length || 0,
      children: (
        <div style={{ padding: '8px 0' }}>
          <Checkbox.Group
            style={{ width: '100%' }}
            value={searchFilters.languages || []}
            onChange={(values) => handleFilterChange('languages', values)}
          >
            <Row gutter={[8, 8]}>
              {[
                { value: 'english', label: 'English', flag: '🇬🇧' },
                { value: 'afrikaans', label: 'Afrikaans', flag: '🇿🇦' },
                { value: 'zulu', label: 'Zulu', flag: '🇿🇦' },
                { value: 'xhosa', label: 'Xhosa', flag: '🇿🇦' },
                { value: 'sotho', label: 'Sotho', flag: '🇿🇦' }
              ].map((lang) => (
                <Col span={24} key={lang.value}>
                  <Checkbox value={lang.value} style={{ fontSize: '14px' }}>
                    <Space>
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </Space>
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </div>
      )
    },
    {
      key: 'gender',
      label: 'Doctor Gender',
      icon: <TeamOutlined style={{ color: token.colorInfo }} />,
      count: searchFilters.gender ? 1 : 0,
      children: (
        <div style={{ padding: '8px 0' }}>
          <Radio.Group
            style={{ width: '100%' }}
            value={searchFilters.gender}
            onChange={(e) => handleFilterChange('gender', e.target.value)}
          >
            <Space direction='vertical' size={12} style={{ width: '100%' }}>
              <Radio value='male' style={{ fontSize: '14px' }}>
                <Space>
                  <span style={{ color: '#1890ff' }}>👨‍⚕️</span>
                  <span>Male Doctor</span>
                </Space>
              </Radio>
              <Radio value='female' style={{ fontSize: '14px' }}>
                <Space>
                  <span style={{ color: '#eb2f96' }}>👩‍⚕️</span>
                  <span>Female Doctor</span>
                </Space>
              </Radio>
              <Radio value='any' style={{ fontSize: '14px' }}>
                <Space>
                  <span>👩‍⚕️👨‍⚕️</span>
                  <span>No Preference</span>
                </Space>
              </Radio>
            </Space>
          </Radio.Group>
        </div>
      )
    }
  ]

  return (
    <Card
      style={{
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '16px',
        border: 'none',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        maxWidth: '400px',
        overflow: 'hidden'
      }}
      bodyStyle={{ padding: 0 }}
    >
      <div style={{ padding: '24px' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title
            level={4}
            style={{
              color: '#1f2937',
              marginBottom: '8px',
              fontSize: '18px',
              fontWeight: '600'
            }}
          >
            Find Your Doctor
          </Title>
          <Text style={{ color: '#6b7280', fontSize: '14px' }}>
            Search and filter to find the perfect healthcare professional
          </Text>
        </div>

        {/* Specialty Selector with Enhanced UI */}
        <div style={{ marginBottom: '16px' }}>
          <Select
            showSearch
            allowClear
            style={{ width: '100%' }}
            placeholder='Select specialty'
            value={searchType}
            onChange={handleSearchTypeChange}
            optionLabelProp='label'
            size='large'
            disabled={isUILoading}
            filterOption={(input, option) =>
              option.children.props.children[1].props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {searchTypes.map((option) => (
              <Option key={option.value} value={option.value} label={option.label}>
                <Space>
                  <span style={{ fontSize: '16px' }}>{option.icon}</span>
                  <div>
                    <div>{option.label}</div>
                    <Text type='secondary' style={{ fontSize: '12px' }}>
                      {option.category}
                    </Text>
                  </div>
                </Space>
              </Option>
            ))}
          </Select>
        </div>

        {/* Enhanced Location Input - Hidden for virtual consultations */}
        {searchType !== 'virtual_consultation' && (
          <div style={{ marginBottom: '20px' }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}
            >
              <Text strong style={{ color: '#374151' }}>
                <Space>
                  <EnvironmentOutlined style={{ color: token.colorPrimary }} />
                  Location
                </Space>
              </Text>
              {location && (
                <Button
                  type='text'
                  size='small'
                  icon={<CloseOutlined />}
                  onClick={() => {
                    setAddress('')
                    setPlaceId('')
                    setLocation(null)
                    setMaxDistance(25)
                    setIsLoadingLocation(false)
                    setHasManuallySetLocation(false) // Allow auto-population again

                    // Save location reset to Redux state
                    updateLocationInRedux({
                      address: '',
                      placeId: '',
                      location: null,
                      maxDistance: 25
                    })
                  }}
                  style={{
                    color: '#6b7280',
                    fontSize: '12px',
                    height: '20px',
                    padding: '0 4px'
                  }}
                  title='Clear location'
                >
                  Clear
                </Button>
              )}
            </div>
            <GoogleAddress
              id='search-location'
              selectProps={{
                placeholder: isLoadingLocation
                  ? 'Loading your address...'
                  : location?.address || 'Search for an address, suburb, or city',
                value: location?.address || undefined,
                onChange: handleLocationChange,
                size: 'large',
                loading: isLoadingLocation,
                style: {
                  borderRadius: '8px',
                  borderColor: '#d1d5db'
                }
              }}
            />
            {location && location.address === patientAddress && (
              <Text
                type='secondary'
                style={{
                  fontSize: '12px',
                  marginTop: '4px',
                  display: 'block',
                  color: token.colorSuccess
                }}
              >
                ✓ Using your profile address
              </Text>
            )}
          </div>
        )}

        {/* Distance Selector - Always visible when location is selected and not virtual consultation */}
        {location && searchType !== 'virtual_consultation' && (
          <div style={{ marginBottom: '20px' }}>
            <Text strong style={{ color: '#374151', marginBottom: '8px', display: 'block' }}>
              <Space>
                <EnvironmentOutlined style={{ color: token.colorWarning }} />
                Maximum Distance
              </Space>
            </Text>

            <div style={{ padding: '0 8px' }}>
              <Slider
                min={5}
                max={100}
                step={5}
                value={maxDistance}
                onChange={(value) => {
                  setMaxDistance(value)
                  updateLocationInRedux({ maxDistance: value })
                }}
                marks={{
                  5: '5km',
                  25: '25km',
                  50: '50km',
                  100: '100km'
                }}
                tooltip={{
                  formatter: (value) => `${value}km radius`
                }}
                trackStyle={{ backgroundColor: token.colorPrimary }}
                handleStyle={{ borderColor: token.colorPrimary }}
              />
              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <Text type='secondary' style={{ fontSize: '12px' }}>
                  Search within <strong>{maxDistance}km</strong> radius
                </Text>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Search Button */}
        <Button
          type='primary'
          icon={<SearchOutlined />}
          onClick={handleNewSearch}
          loading={isUILoading}
          block
          size='large'
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            borderColor: 'transparent',
            fontWeight: '600',
            height: '48px',
            borderRadius: '12px',
            fontSize: '16px',
            marginBottom: '24px',
            boxShadow: '0 4px 16px rgba(102, 126, 234, 0.4)'
          }}
        >
          {location && searchType !== 'virtual_consultation' ? 'Find Nearby Doctors' : 'Find Doctors'}
        </Button>

        {location && searchType !== 'virtual_consultation' && (
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <Text type='secondary' style={{ fontSize: '12px' }}>
              <EnvironmentOutlined style={{ color: token.colorSuccess, marginRight: '4px' }} />
              Searching within {maxDistance}km of: {location.address}
            </Text>
          </div>
        )}

        <Divider style={{ margin: '24px 0', borderColor: '#e5e7eb' }} />

        {/* Enhanced Refine Filters Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}
        >
          <Space>
            <FilterOutlined style={{ color: token.colorPrimary }} />
            <Title
              level={5}
              style={{
                color: '#374151',
                margin: 0,
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              Refine Search
            </Title>
            {activeFiltersCount > 0 && (
              <Badge
                count={activeFiltersCount}
                style={{
                  backgroundColor: token.colorPrimary,
                  fontSize: '11px',
                  height: '18px',
                  minWidth: '18px',
                  lineHeight: '18px'
                }}
              />
            )}
          </Space>
          {activeFiltersCount > 0 && (
            <Tooltip title='Clear all filters'>
              <Button
                type='text'
                size='small'
                icon={<ClearOutlined />}
                onClick={clearAllFilters}
                style={{
                  color: '#6b7280',
                  fontSize: '12px'
                }}
              >
                Clear All
              </Button>
            </Tooltip>
          )}
        </div>

        {/* Enhanced Filter Sections */}
        <Collapse
          ghost
          expandIcon={({ isActive }) => (
            <CaretRightOutlined
              rotate={isActive ? 90 : 0}
              style={{
                color: token.colorPrimary,
                fontSize: '12px'
              }}
            />
          )}
          style={{ background: 'transparent' }}
          items={filterSections.map((section) => ({
            key: section.key,
            label: (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  paddingRight: '20px'
                }}
              >
                <Space>
                  {section.icon}
                  <Text strong style={{ fontSize: '14px', color: '#374151' }}>
                    {section.label}
                  </Text>
                </Space>
                {section.count > 0 && (
                  <Badge
                    count={section.count}
                    size='small'
                    style={{
                      backgroundColor: '#f3f4f6',
                      color: token.colorPrimary,
                      border: `1px solid ${token.colorPrimary}`,
                      fontSize: '10px'
                    }}
                  />
                )}
              </div>
            ),
            children: section.children,
            style: {
              marginBottom: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              borderRadius: '8px',
              border: '1px solid #f0f0f0'
            }
          }))}
        />

        {/* Active Filters Display */}
        {activeFiltersCount > 0 && (
          <div
            style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '8px'
            }}
          >
            <Text strong style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', display: 'block' }}>
              ACTIVE FILTERS ({activeFiltersCount})
            </Text>
            <Space wrap size={[4, 4]}>
              {searchFilters.consultType?.map((type) => (
                <Tag
                  key={type}
                  closable
                  onClose={() => {
                    const newTypes = searchFilters.consultType.filter((t) => t !== type)
                    handleFilterChange('consultType', newTypes)
                  }}
                  style={{
                    margin: '2px',
                    fontSize: '11px',
                    backgroundColor: `${getFilterColor('consultType', type)}15`,
                    borderColor: getFilterColor('consultType', type),
                    color: getFilterColor('consultType', type),
                    fontWeight: '500'
                  }}
                >
                  {formatFilterValue('consultType', type)}
                </Tag>
              ))}
              {searchFilters.timeSlot && (
                <Tag
                  closable
                  onClose={() => handleFilterChange('timeSlot', '')}
                  style={{
                    margin: '2px',
                    fontSize: '11px',
                    backgroundColor: `${getFilterColor('timeSlot')}15`,
                    borderColor: getFilterColor('timeSlot'),
                    color: getFilterColor('timeSlot'),
                    fontWeight: '500'
                  }}
                >
                  {formatFilterValue('timeSlot', searchFilters.timeSlot)}
                </Tag>
              )}
              {searchFilters.languages?.map((lang) => (
                <Tag
                  key={lang}
                  closable
                  onClose={() => {
                    const newLangs = searchFilters.languages.filter((l) => l !== lang)
                    handleFilterChange('languages', newLangs)
                  }}
                  style={{
                    margin: '2px',
                    fontSize: '11px',
                    backgroundColor: `${getFilterColor('languages')}15`,
                    borderColor: getFilterColor('languages'),
                    color: getFilterColor('languages'),
                    fontWeight: '500'
                  }}
                >
                  {formatFilterValue('languages', lang)}
                </Tag>
              ))}
              {searchFilters.gender && (
                <Tag
                  closable
                  onClose={() => handleFilterChange('gender', '')}
                  style={{
                    margin: '2px',
                    fontSize: '11px',
                    backgroundColor: `${getFilterColor('gender')}15`,
                    borderColor: getFilterColor('gender'),
                    color: getFilterColor('gender'),
                    fontWeight: '500'
                  }}
                >
                  {formatFilterValue('gender', searchFilters.gender)}
                </Tag>
              )}
            </Space>
          </div>
        )}
      </div>
    </Card>
  )
}

export default SearchRefinement
